import { Download, FileSpreadsheet } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  METRIC_DICTIONARY_PATH,
  REPORT_BUILDER_PATH,
  type AnalyticsReport,
  type CountFigure,
  type LatencyFigure,
  type MetricKey,
  type RateFigure,
  type ResolvedAnalyticsFilters,
} from '@nest-recruit/shared';
import { Card } from '../../../components/Card';
import { EMPTY, formatDate, formatNumber } from '../../../lib/format';
import { DeepLink } from '../../notifications/components/DeepLink';
import { analyticsDrillHref, analyticsUrlOf, exportHref } from '../api/analytics-api';
import { percent, periodText } from '../report-format';
import styles from '../Reports.module.css';
import { METRIC_TONES } from '../metric-tones';

/** A count is a door to its records (06-qol §12, F17.02); nothing to open reads as a quiet 0. */
export function DrillCount({ figure, report, filters }: { readonly figure: CountFigure; readonly report: AnalyticsReport; readonly filters: ResolvedAnalyticsFilters }): ReactNode {
  if (figure.value === 0) return <span className={styles.zero}>0</span>;
  return (
    <DeepLink to={analyticsDrillHref(filters, report, figure.drill)} className={styles.count}>
      {formatNumber(figure.value)}
    </DeepLink>
  );
}

/** "42% (21 of 50)", each count opening its records. */
export function DrillRate({ figure, report, filters }: { readonly figure: RateFigure; readonly report: AnalyticsReport; readonly filters: ResolvedAnalyticsFilters }): ReactNode {
  if (figure.denominator === 0) return <span className={styles.zero}>{EMPTY}</span>;
  return (
    <span className={styles.rate}>
      <span className={styles.figure}>{percent(figure.share)}</span>
      <span className={styles.cellMeta}>
        <DeepLink to={analyticsDrillHref(filters, report, `${figure.drill}:num`)} className={styles.inlineLink}>
          {formatNumber(figure.numerator)}
        </DeepLink>{' '}
        of{' '}
        <DeepLink to={analyticsDrillHref(filters, report, `${figure.drill}:den`)} className={styles.inlineLink}>
          {formatNumber(figure.denominator)}
        </DeepLink>
      </span>
    </span>
  );
}

/** 06-qol §12: elapsed and active, always both — "4.5 days · 3 working days". */
export function DrillLatency({ figure, report, filters }: { readonly figure: LatencyFigure; readonly report: AnalyticsReport; readonly filters: ResolvedAnalyticsFilters }): ReactNode {
  if (figure.count === 0) return <span className={styles.zero}>{EMPTY}</span>;
  return (
    <DeepLink to={analyticsDrillHref(filters, report, figure.drill)} className={styles.latency}>
      <span className={styles.figure}>{figure.medianActiveDays ?? EMPTY} working days</span>
      <span className={styles.cellMeta}>
        {figure.medianElapsedDays ?? EMPTY} days elapsed · {formatNumber(figure.count)} counted
      </span>
    </DeepLink>
  );
}

/** "How this is counted" — every figure links to its definition (06-qol §12). */
export function HowCounted({ metric, label = 'How this is counted' }: { readonly metric: MetricKey; readonly label?: string }): ReactNode {
  return (
    <DeepLink to={`${METRIC_DICTIONARY_PATH}#${metric}`} className={styles.definition}>
      {label}
    </DeepLink>
  );
}

/** The period and the dictionary version every report states (§L27, D12). */
export function DictionaryLine({ filters, version, publishedOn }: { readonly filters: ResolvedAnalyticsFilters; readonly version: number; readonly publishedOn: string }): ReactNode {
  return (
    <p className={styles.meta}>
      {periodText(filters)} · computed under{' '}
      <DeepLink to={METRIC_DICTIONARY_PATH} className={styles.inlineLink}>
        metric dictionary v{version}
      </DeepLink>{' '}
      (published {formatDate(`${publishedOn}T00:00:00Z`)}).
    </p>
  );
}

/** F17.04 and RPT-06 from any report screen: download, or open it in the builder to save and schedule. */
export function ExportLinks({ report, filters }: { readonly report: AnalyticsReport; readonly filters: ResolvedAnalyticsFilters }): ReactNode {
  const url = analyticsUrlOf(filters);
  const builder = new URLSearchParams({ report });
  for (const [name, value] of Object.entries(url)) if (typeof value === 'string') builder.set(name, value);
  return (
    <span className={styles.headerActions}>
      <a className={styles.linkButton} href={exportHref(report, url, 'xlsx', [])} download>
        <Download size={14} aria-hidden /> Excel
      </a>
      <a className={styles.linkButton} href={exportHref(report, url, 'csv', [])} download>
        <Download size={14} aria-hidden /> CSV
      </a>
      <DeepLink to={`${REPORT_BUILDER_PATH}?${builder.toString()}`} className={styles.linkButton}>
        <FileSpreadsheet size={14} aria-hidden /> Save or schedule
      </DeepLink>
    </span>
  );
}

/** One titled section with its definition link, as on the command center. */
export function ReportSection({ id, title, meta, metric, children }: { readonly id: string; readonly title: string; readonly meta?: string; readonly metric: MetricKey; readonly children: ReactNode }): ReactNode {
  return (
    <div id={id} className={styles.section}>
      <Card title={title} {...(meta ? { meta } : {})} tone={METRIC_TONES[metric]} action={<HowCounted metric={metric} />}>
        {children}
      </Card>
    </div>
  );
}
